import { clearTokens, request, setTokens } from "./http";
import type { InvitePreview, Me } from "./types";

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface LoginResult {
  requires_2fa: boolean;
  pre_auth_token?: string | null;
}

interface LoginApiResponse {
  requires_2fa?: boolean;
  pre_auth_token?: string | null;
  access_token?: string | null;
  refresh_token?: string | null;
}

export async function login(email: string, password: string): Promise<LoginResult> {
  const res = await request<LoginApiResponse>("/auth/login", {
    method: "POST",
    body: { email, password },
    anonymous: true,
  });
  if (res.requires_2fa) {
    return { requires_2fa: true, pre_auth_token: res.pre_auth_token };
  }
  setTokens(res.access_token as string, res.refresh_token as string);
  return { requires_2fa: false };
}

/** Second login step: exchange the pre-auth token + a TOTP/recovery code for
 *  a real session. */
export async function verify2fa(preAuthToken: string, code: string): Promise<void> {
  const tokens = await request<TokenResponse>("/auth/verify-2fa", {
    method: "POST",
    body: { pre_auth_token: preAuthToken, code },
    anonymous: true,
  });
  setTokens(tokens.access_token, tokens.refresh_token);
}

export interface TotpEnroll {
  secret: string;
  provisioning_uri: string;
  expires_in_seconds: number;
}

/** Phase 1 of enabling 2FA: mint a pending secret + otpauth URI (render the QR
 *  from it client-side). */
export function enrollTotp(): Promise<TotpEnroll> {
  return request("/me/totp/enroll", { method: "POST" });
}

/** Phase 2: confirm a code, turn 2FA on, and receive one-time recovery codes. */
export function verifyTotp(code: string): Promise<{ enabled: boolean; recovery_codes: string[] }> {
  return request("/me/totp/verify", { method: "POST", body: { code } });
}

/** Turn 2FA off — needs a current TOTP or recovery code. */
export function disableTotp(code: string): Promise<void> {
  return request("/me/totp/disable", { method: "POST", body: { code } });
}

/** Self-serve signup. The server provisions a personal tenant-of-one and
 *  returns a live session, so we store the tokens and the caller is
 *  immediately logged in (straight into onboarding, no separate login). */
export async function signup(
  email: string,
  password: string,
  fullName: string,
): Promise<void> {
  const tokens = await request<TokenResponse>("/auth/signup", {
    method: "POST",
    body: { email, password, full_name: fullName },
    anonymous: true,
  });
  setTokens(tokens.access_token, tokens.refresh_token);
}

export async function logout(): Promise<void> {
  const refresh = localStorage.getItem("discentem.refresh");
  try {
    if (refresh) {
      await request<void>("/auth/logout", {
        method: "POST",
        body: { refresh_token: refresh },
      });
    }
  } finally {
    // Local session dies even if the server call failed — the refresh
    // token stays revocable server-side via password reset.
    clearTokens();
  }
}

export function forgotPassword(email: string): Promise<{ ok: boolean; message: string }> {
  return request("/auth/forgot-password", {
    method: "POST",
    body: { email },
    anonymous: true,
  });
}

export function resetPassword(token: string, newPassword: string): Promise<{ ok: boolean; message: string }> {
  return request("/auth/reset-password", {
    method: "POST",
    body: { token, new_password: newPassword },
    anonymous: true,
  });
}

export function previewInvite(token: string): Promise<InvitePreview> {
  return request(`/auth/invite/${token}`, { anonymous: true });
}

export function acceptInvite(token: string, newPassword: string): Promise<{ ok: boolean; message: string }> {
  return request("/auth/accept-invite", {
    method: "POST",
    body: { token, new_password: newPassword },
    anonymous: true,
  });
}

export function fetchMe(): Promise<Me> {
  return request("/me/");
}

/** Remember that the guided tour was finished or skipped, so it doesn't
 *  re-run on this learner's next sign-in (on any device). */
export function completeTour(): Promise<Me> {
  return request("/me/tour-complete", { method: "POST" });
}

/** Module codes active for my tenant — lets a surface show what's locked
 *  BEFORE the learner clicks into a 403. */
export function myModules(): Promise<string[]> {
  return request("/me/modules");
}

/** Self-service profile edit (name / phone / language / voice / email
 *  opt-out). Returns the refreshed Me. */
export function updateProfile(body: {
  full_name?: string;
  phone?: string;
  language_preference?: string;
  /** Which voice the platform speaks in: "female" | "male". */
  voice_preference?: string;
  notification_email_optout?: boolean;
}): Promise<Me> {
  return request("/me/", { method: "PATCH", body });
}

/** Change my own password — the current password is required. */
export function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  return request("/me/change-password", {
    method: "POST",
    body: { current_password: currentPassword, new_password: newPassword },
  });
}
